/**
 * O que faz: Analisa leads por dia da semana com barras horizontais e destaque do melhor dia.
 * Onde usa: Dashboard principal na seção de gráficos.
 * Conexões: Recebe leads filtrados por período e processa a distribuição internamente.
 * Tabelas: Não acessa banco; consome leads já carregados.
 * Data: 11/06/2026
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Calendar } from 'lucide-react';
import { Lead } from '@/hooks/useSupabaseLeads';
import { useEffect, useMemo, useState } from 'react';
import { buildWeekdaySeries } from '@/features/dashboard/report/dashboardReportAnalytics';
import { Button } from '@/components/ui/button';
import { selecionarSemana, type FiltroSemanaDashboard } from '@/features/dashboard/data/dashboardSeries';
import type { SemanaDashboard } from '@/features/dashboard/data/dashboardContracts';

interface WeeklyAnalysisCardProps {
  leads: Lead[];
  /** Semana agregada pelo servidor; evita derivar a visualização a partir dos leads completos. */
  semanaResumo?: SemanaDashboard[];
  title: string;
  description: string;
  filtro?: FiltroSemanaDashboard;
  onFiltroChange?: (filtro: FiltroSemanaDashboard) => void;
}

interface WeeklyData {
  dia: string;
  nomeCurto: string;
  leads: number;
  percentual: number;
}

type LeadComAnuncioLegado = Lead & { anuncio?: string | null };

export const WeeklyAnalysisCard = ({ leads, semanaResumo, title, description, filtro: filtroControlado, onFiltroChange }: WeeklyAnalysisCardProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [filtroInterno, setFiltroInterno] = useState<FiltroSemanaDashboard>('todos');
  const filtro = filtroControlado ?? filtroInterno;
  const alterarFiltro = (proximoFiltro: FiltroSemanaDashboard) => {
    // Mantém uso local como padrão e permite que o dashboard compartilhe a seleção quando necessário.
    if (filtroControlado === undefined) setFiltroInterno(proximoFiltro);
    onFiltroChange?.(proximoFiltro);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 640px)');
    const handleChange = () => setIsMobile(media.matches);
    handleChange();

    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  const weeklyData: WeeklyData[] = useMemo(() => {
    if (semanaResumo) return selecionarSemana(semanaResumo, filtro).itens;

    // Usa o mesmo predicado de anúncio do relatório para reconhecer ad_name e anuncio legado.
    const serieTodos = buildWeekdaySeries(leads);
    const serieAnuncios = buildWeekdaySeries(leads.filter(lead => {
          const leadComAnuncio = lead as LeadComAnuncioLegado;
          return Boolean(lead.ad_name?.trim() || leadComAnuncio.anuncio?.trim());
        }));
    const serie = serieTodos.map(item => ({
      dia: item.dayIndex,
      todos: item.leads,
      anuncios: serieAnuncios[item.dayIndex]?.leads ?? 0,
    }));
    return selecionarSemana(serie, filtro).itens.map(item => ({
      dia: item.dia,
      nomeCurto: item.nomeCurto,
      leads: item.leads,
      percentual: item.percentual,
    }));
  }, [filtro, leads, semanaResumo]);

  const maxLeads = weeklyData.reduce((max, d) => Math.max(max, d.leads), 0);
  const bestDays = weeklyData.filter(d => d.leads === maxLeads && maxLeads > 0);

  const formatDia = (dia: string) => {
    const weeklyItem = weeklyData.find((item) => item.dia === dia);
    return isMobile ? weeklyItem?.nomeCurto ?? dia : dia;
  };
  const chartHeight = isMobile ? 232 : 280;
  const chartMargin = isMobile
    ? { top: 4, right: 12, left: 0, bottom: 4 }
    : { top: 4, right: 16, left: 8, bottom: 4 };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-lg">
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
            {item.leads} {item.leads === 1 ? 'lead' : 'leads'}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{item.percentual}% do total</p>
        </div>
      );
    }
    return null;
  };

  const hasData = weeklyData.length > 0 && weeklyData.some(d => d.leads > 0);

  return (
    <div className="flex flex-col bg-card rounded-2xl border border-border p-4 sm:p-6 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)]">
      {/* Header */}
      <div className="mb-4 sm:mb-5">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Filtrar distribuição por origem" className="flex items-center gap-1 rounded-full border border-border bg-muted/50 p-0.5">
            <Button
              type="button"
              variant={filtro === 'todos' ? 'secondary' : 'ghost'}
              size="sm"
              className="min-h-11 rounded-full px-3 text-xs"
              aria-pressed={filtro === 'todos'}
              onClick={() => alterarFiltro('todos')}
            >
              Todos
            </Button>
            <Button
              type="button"
              variant={filtro === 'anuncios' ? 'secondary' : 'ghost'}
              size="sm"
              className="min-h-11 rounded-full px-3 text-xs"
              aria-pressed={filtro === 'anuncios'}
              onClick={() => alterarFiltro('anuncios')}
            >
              Somente anúncios
            </Button>
          </div>
        </div>

        {hasData && bestDays.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground">
              <Calendar className="h-3 w-3" aria-hidden="true" />
              {bestDays.length === 1 ? 'Melhor dia' : 'Maior volume'}: {bestDays.map(dia => dia.dia).join(', ')}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums" aria-live="polite">
              {bestDays[0].leads} leads ({bestDays[0].percentual}%)
            </span>
          </div>
        )}
      </div>

      {/* Gráfico — melhor dia em cor cheia, demais em tom suave */}
      <div className="w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={weeklyData} layout="vertical" margin={chartMargin} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="4 4" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="dia"
                tick={{ fontSize: isMobile ? 11 : 12, fill: 'hsl(var(--foreground))' }}
                width={isMobile ? 42 : 68}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatDia}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(16,185,129,0.06)' }} />
              <Bar dataKey="leads" radius={[0, 6, 6, 0]} maxBarSize={isMobile ? 20 : 26}>
                {weeklyData.map((entry) => (
                  <Cell
                    key={entry.dia}
                    // Mesmo código visual do gráfico de origem: dia líder em lima profundo, demais em musgo neutro.
                    fill={entry.leads === maxLeads ? '#8fb83a' : '#e3e9dc'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-56 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <Calendar className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted-foreground">
                {filtro === 'anuncios' ? 'Nenhum anúncio no período selecionado' : 'Nenhum lead no período selecionado'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Rodapé estatístico */}
      {hasData && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold tabular-nums text-foreground">{weeklyData.reduce((total, item) => total + item.leads, 0)}</span>{' '}
            {filtro === 'anuncios' ? 'leads de anúncios' : 'leads'} analisados no período
          </p>
        </div>
      )}
    </div>
  );
};
